import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Home,
  FileText,
  Users,
  ChevronLeft,
  ChevronRight,
  X,
  Settings,
  MessageCircle,
  LogOut,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import authService from "../../services/common/authService";
import OrganizationService from "../../services/organization/OrganizationService";
import { baseURL } from "../../services/common/endPoints";

// Menu structure (labels/urls) for sidebar and route meta. Single source for page titles.
const baseMenuData = [
  { id: "dashboard", label: "Dashboard", url: "/dashboard" },
  { id: "organization", label: "Organization Info", url: "/organization" },
  {
    id: "export",
    label: "Export documentation",
    submenu: [
      { id: "products", label: "Products", url: "/products" },
      { id: "quotation-documents", label: "Quotation Documents", url: "/quotation-documents" },
      { id: "performa-documents", label: "Performa Documents", url: "/performa-documents" },
      { id: "invoice-documents", label: "Invoice Documents", url: "/invoice-documents" },
      { id: "bl-draft-documents", label: "BL Draft Documents", url: "/bl-draft-documents" },
      { id: "vgm-documents", label: "VGM Documents", url: "/vgm-documents" },
      { id: "annexure-documents", label: "Annexure Documents", url: "/annexure-documents" },
    ],
  },
  {
    id: "master",
    label: "Master",
    submenu: [
      { id: "units", label: "Units", url: "/units" },
      { id: "country", label: "Country", url: "/country" },
      { id: "containerType", label: "Container Type", url: "/containerType" },
      { id: "states", label: "States", url: "/states" },
      { id: "currency", label: "Currency", url: "/currency" },
      { id: "packageType", label: "Package Type", url: "/packageType" },
      { id: "city", label: "City", url: "/city" },
      { id: "paymentTerms", label: "Payment Terms", url: "/paymentTerms" },
      { id: "ports", label: "Ports", url: "/ports" },
      { id: "customers", label: "Customers", url: "/customers" },
    ],
  },
];

const publicRouteMap = {
  "/": "Home",
  "/login": "Login",
  "/signup": "Sign Up",
  "/notfound": "Page Not Found",
};

function buildRouteLabelMap() {
    const map = { ...publicRouteMap };
    baseMenuData.forEach((item) => {
    if (item.url) map[item.url] = item.label;
    if (item.submenu) item.submenu.forEach((sub) => { map[sub.url] = sub.label; });
  });
  map["/employees"] = "Employees";
  map["/support"] = "Support";
  return map;
}

const routeLabelMap = buildRouteLabelMap();
const pathPrefixes = Object.keys(routeLabelMap)
  .filter((p) => p !== "/")
  .sort((a, b) => b.length - a.length);

/**
 * Get page title label for pathname. Used by PageMeta for document title and meta tags.
 */
export function getRouteLabel(pathname) {
  const path = pathname.replace(/\/$/, "") || "/";
  if (routeLabelMap[path]) return routeLabelMap[path];
    for (const prefix of pathPrefixes) {
    if (path === prefix || path.startsWith(prefix + "/")) return routeLabelMap[prefix];
  }
  return routeLabelMap["/"] || "Home";
}

const iconById = {
  dashboard: <Home className="h-4 w-4" />,
  organization: <Users className="h-4 w-4" />,
  export: <FileText className="h-4 w-4" />,
  master: <Settings className="h-4 w-4" />,
  employees: <Users className="h-4 w-4" />,
  support: <MessageCircle className="h-4 w-4" />,
};

const Sidebar = ({
  activeMenu,
  setActiveMenu,
  isSidebarOpen,
  setIsSidebarOpen,
  isCollapsed,
  setIsCollapsed,
  onLogout,
}) => {
  const [collapsedItems, setCollapsedItems] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [organizationLogo, setOrganizationLogo] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Derive activeMenu key from current path (e.g. /country -> "master/country")
  const getActiveMenuFromPath = useCallback((pathname) => {
    const path = pathname.replace(/\/$/, "") || "/";
    for (const item of baseMenuData) {
      if (item.url && path === item.url) return item.id;
      if (item.submenu) {
        for (const sub of item.submenu) {
          if (sub.url && path === sub.url) return `${item.id}/${sub.id}`;
        }
      }
    }
    if (path === "/employees") return "employees";
    if (path === "/support") return "support";
    return null;
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("activeMenu");
    if (saved) setActiveMenu(saved);
    
    // Check if user is admin
    setIsAdmin(authService.isAdmin());
    
    // Fetch organization logo/document
    fetchOrganizationLogo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync active menu and expand parent when URL changes (direct nav or refresh)
  useEffect(() => {
    const key = getActiveMenuFromPath(location.pathname);
    if (key) {
      setActiveMenu(key);
      localStorage.setItem("activeMenu", key);
      const parentId = key.includes("/") ? key.split("/")[0] : null;
      if (parentId) {
        setCollapsedItems((p) => ({ ...p, [parentId]: true }));
      }
    }
  }, [location.pathname, getActiveMenuFromPath, setActiveMenu]);

  const fetchOrganizationLogo = async () => {
    try {
      const response = await OrganizationService.getOrganization();
      const data = response.data?.data || {};
      
      // Prefer organization_document_url, fallback to logo_url
      if (data.organization_document_url) {
        setOrganizationLogo(`${baseURL}${data.organization_document_url}`);
      } else if (data.logo_url) {
        setOrganizationLogo(data.logo_url);
      }
    } catch (error) {
      console.error("Error fetching organization logo:", error);
      // Keep default logo on error
    }
  };

  useEffect(() => {
    if (isCollapsed) {
      setCollapsedItems({});
    }
  }, [isCollapsed]);

  // Build menu items based on user role - recalculates when isAdmin changes
    const getMenuItems = useCallback(() => {
        const baseMenuItems = baseMenuData.map((item) => ({
      ...item,
            icon: iconById[item.id],
      submenu: item.submenu ? [...item.submenu] : undefined,
    }));

    if (isAdmin) {
      baseMenuItems.push({
        id: "employees",
        label: "Employees",
        url: "/employees",
          icon: iconById.employees,
      });
    }

    baseMenuItems.push({
      id: "support",
      label: "Support",
      url: "/support",
        icon: iconById.support,
    });

    return baseMenuItems;
  }, [isAdmin]);

  // Use useMemo to recalculate menuItems whenever getMenuItems changes
  const menuItems = useMemo(() => getMenuItems(), [getMenuItems]);

  const toggleSubmenu = (id) => {
    // Auto expand if sidebar is collapsed
    if (isCollapsed) setIsCollapsed(false);

    setCollapsedItems((p) => ({ ...p, [id]: !p[id] }));
  };

  const handleMenuItemClick = (parentId, item) => {
    if (isCollapsed) setIsCollapsed(false);

    const fullPath = parentId ? `${parentId}/${item.id}` : item.id;

    localStorage.setItem("activeMenu", fullPath);
      setActiveMenu(fullPath);
      navigate(item.url);
    if (window.innerWidth < 768) setIsSidebarOpen(false);
  };

  const renderMenuItem = (item, isSubmenu = false, parentId = null) => {
    const hasSubmenu = item.submenu?.length > 0;
    const isOpen = collapsedItems[item.id] === true && hasSubmenu;
    // activeMenu is stored as "parentId/itemId" for submenus, so compare with full path for subitems
    const activeKey = parentId ? `${parentId}/${item.id}` : item.id;
    const isActive = activeMenu === activeKey;

    return (
      <div key={item.id}>
        <button
          onClick={() =>
            hasSubmenu
              ? toggleSubmenu(item.id)
              : handleMenuItemClick(parentId, item)
          }
          className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-all
            ${
              !isSubmenu
                ? isActive
                  ? "bg-surface text-theme border-l-4 border-theme"
                  : "text-theme hover:opacity-90"
                : isActive
                  ? "bg-surface text-theme pl-8"
                  : "text-theme hover:bg-gray-50 pl-8"
            }
          `}
          title={isCollapsed && !isSubmenu ? item.label : ""}
        >
          <span className="flex items-center">
            {!isSubmenu && item.icon}
            {!isSubmenu && !isCollapsed && (
              <span className="ml-2 font-medium">{item.label}</span>
            )}
            {isSubmenu && <span>{item.label}</span>}
          </span>

          {hasSubmenu && !isCollapsed && (
            <ChevronRight
              className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`}
            />
          )}
        </button>

        {hasSubmenu && isOpen && !isCollapsed && (
          <div className="border-theme">
            {item.submenu.map((subitem) =>
              renderMenuItem(subitem, true, item.id),
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Overlay Mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 md:hidden z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-screen sidebar shadow-md z-50 w-60 md:hidden transition-transform duration-300 flex flex-col ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-12 border-b px-4 flex-shrink-0">
          {/* Logo */}
          {organizationLogo ? (
            <img
              src={organizationLogo}
              alt="Company Logo"
              className="h-8 object-contain"
              onError={(e) => {
                e.target.src = "/CompanyLogo.png";
              }}
            />
          ) : (
            <img
              src="/CompanyLogo.png"
              alt="Company Logo"
              className="h-8 object-contain"
            />
          )}
          <button onClick={() => setIsSidebarOpen(false)}>
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-hide">
          {menuItems.map((i) => renderMenuItem(i))}
        </nav>
        <div className="flex-shrink-0 border-t border-theme/20 p-2">
          <button
            onClick={() => {
              onLogout?.();
              if (window.innerWidth < 768) setIsSidebarOpen(false);
            }}
            className="w-full flex items-center px-3 py-2 rounded-md text-sm text-theme hover:bg-surface transition-colors"
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            <span className="ml-2 font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex fixed top-0 left-0 h-screen sidebar shadow-md z-50 flex-col transition-all duration-300 ${
          isCollapsed ? "w-16" : "w-60"
        }`}
      >
        {/* Header + Collapse Button */}
        <div className="flex items-center justify-center h-12 border-b px-4 relative">
          {/* Collapse Button at Top */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="absolute right-2 top-2 p-1 rounded-md bg-white border text-gray-700 hover:bg-gray-200 text-theme bg-surface border-theme"
            title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>

          {/* Logo */}
          {!isCollapsed ? (
            organizationLogo ? (
              <img
                src={organizationLogo}
                alt="Company Logo"
                className="h-12 object-contain"
                onError={(e) => {
                  e.target.src = "/CompanyLogo.png";
                }}
              />
            ) : (
              <img
                src="/CompanyLogo.png"
                alt="Company Logo"
                className="h-12 object-contain"
              />
            )
          ) : (
            organizationLogo ? (
              <img
                src={organizationLogo}
                alt="Company Logo"
                className="h-12 w-12 object-contain"
                onError={(e) => {
                  e.target.src = "/CompanyCollapsLogo.png";
                }}
              />
            ) : (
              <img
                src="/CompanyCollapsLogo.png"
                alt="Company Logo"
                className="h-12 w-12 object-contain"
              />
            )
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-hide">
          {menuItems.map((i) => renderMenuItem(i))}
        </nav>
        <div className="flex-shrink-0 border-t border-theme/20 p-2">
          <button
            onClick={onLogout}
            className={`w-full flex items-center rounded-md text-sm text-theme hover:bg-surface transition-colors ${
              isCollapsed ? "justify-center px-2 py-2" : "px-3 py-2"
            }`}
            title="Logout"
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            {!isCollapsed && <span className="ml-2 font-medium">Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
